"use client";
import React from "react";
import { useTheme } from "@guberno/utils/theme";
import { AppContext } from "@guberno/utils/context";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { WcStack } from "@bpipartners/webui";

/**
 * Floor low enough that the page never forces a horizontal overflow: must fit
 * beside the expanded drawer (280) + margins (48) at the 1200px desktop target
 * (872 available) and iPad landscape 1024 (696 available). The shared
 * minimumPageWidth (1000) cannot — it forced the shell 128px past a 1200px
 * viewport.
 */
const PAGE_MIN_WIDTH = 680;

export const MainPageLayout = ({
  children,
  isPresentationView = false,
}: {
  children: React.ReactNode;
  isPresentationView?: boolean;
}) => {
  const theme = useTheme();
  const { localIsDrawerExpandedState } = React.useContext(AppContext);
  const [width, setWidth] = React.useState(theme.dimensions.baseWidth ? theme.dimensions.baseWidth : "100%");

  React.useEffect(() => {
    // The page sits beside the drawer with marginX 12 (= 24px per side in the
    // 2px spacing theme). Those 48px must be subtracted from 100vw as well,
    // otherwise the drawer + margins + page row is 48px wider than the
    // viewport and the right edge (e.g. a dashboard feed column) is pushed
    // out of view.
    const chrome = theme.dimensions.margin * 6; // 48
    const drawer = isPresentationView
      ? 100
      : localIsDrawerExpandedState
      ? theme.dimensions.totalAppDrawerWidth
      : theme.dimensions.appDrawerCollapsedWidth;
    setWidth(`calc(100vw - ${drawer + chrome}px)`);
  }, [isPresentationView, localIsDrawerExpandedState, theme.dimensions]);

  return (
    <WcStack
      id="mainPageLayout"
      width={width}
      minWidth={PAGE_MIN_WIDTH}
      sx={{
        overflowX: "auto",
        overflowY: "hidden",
        marginX: 12,
      }}
    >
      {children}
    </WcStack>
  );
};
